import request, { _GET, _POST } from "@/services/request";
import {
  InfluencerReq,
  InfluencerTalentRequest,
  InvitePermissionListInviteesRequest,
  InvitePermissionListRequest,
  InvitePermissionListInviteesRes,
  InvitePermissionListRes,
  OperationDetailRecord,
  OperationDetailRequest,
  OperationStateRequest,
  InvitePermissionStatusRequest,
  ModSettingResponse,
} from "@/data-contracts";

/** 达人全局领样配置 */
export type TalentSampleConfig = {
  thresholdExempt?: boolean;
  activeApplyLimit?: number;
  sampleApplyBlocked?: boolean;
  updatedAt?: string;
  updatedBy?: string;
};

export type TalentFansFeature = {
  featureAge?: string;
  featureSex?: string;
  perUvPrice?: string;
  featureAgeRatio?: number;
  featureProvince?: string;
  featureSexRatio?: number;
  perUvPriceRatio?: number;
  userFirstCateName?: string;
  featureEcomUserLevel?: string;
  featureProvinceRatio?: number;
  userFirstCateNameRatio?: number;
  featureEcomUserLevelRatio?: number;
};

export type TalentPromotionSceneDetail = {
  ratio?: number;
  sceneDesc?: number;
};

/** 粉丝特征按 sceneType：""=全部 / 1=直播 / 2=短视频 */
export type TalentFansFeatureSceneTypeGroup = {
  sceneType?: string;
  data?: TalentFansFeature;
};

export type TalentSalesOverview = {
  gmv?: string;
  payUv?: string;
  fansNumber?: string;
  perUvPrice?: string;
  payProductCnt?: string;
  repeatCustomeUv?: string;
  promotionSceneDetailList?: TalentPromotionSceneDetail[];
};

/** 按 dayType 一组：带货概览 + 粉丝特征（按 sceneType） */
export type TalentLeagueDayTypeGroup = {
  dayType?: string;
  salesOverview?: TalentSalesOverview;
  fansFeature?: TalentFansFeatureSceneTypeGroup[];
};

export type TalentLeagueData = {
  dayTypeGroups?: TalentLeagueDayTypeGroup[];
  topDaren?: number;
  windowsScore?: string;
  /** 未关联联盟数据时，手动填写的带货销售额（元） */
  manualGmv?: string;
  /** 未关联联盟数据时，手动填写的客单价（元） */
  manualPerUvPrice?: string;
  /** 未关联联盟数据时，手动填写的粉丝数（人） */
  manualFansNumber?: string;
};

export type TalentDetail = OperationDetailRecord & {
  leagueData?: TalentLeagueData;
  /** 是否已关联联盟原始数据 */
  dataAssociated?: boolean;
  sampleConfig?: TalentSampleConfig;
};

export type UpdateTalentSampleConfigReq = {
  participantId: string;
  thresholdExempt?: boolean;
  activeApplyLimit?: number;
  sampleApplyBlocked?: boolean;
};

export type SaveTalentLeagueDataReq = {
  participantId: string;
  /** 近30天带货销售额，单位：元 */
  gmv: string;
  /** 客单价，单位：元 */
  perUvPrice: string;
  /** 粉丝数，单位：人 */
  fansNumber: string;
};

/**
 * 达人列表
 * GET /admin/talent/list
 */
export const getInfluencerTalentList = (params?: InfluencerReq) => {
  return request<InfluencerTalentRequest, InfluencerReq>(_GET, "/admin/talent/list")(params || {});
};

/**
 * 达人详情
 * POST /admin/talent/detail
 */
export const getInfluencerDetail = (params: { participantId: string }) => {
  return request<TalentDetail, { participantId: string }>(_POST, "/admin/talent/detail")(params);
};

/**
 * 更新达人全局领样配置
 * POST /admin/talent/sample-config/update
 */
export const updateTalentSampleConfig = (params: UpdateTalentSampleConfigReq) => {
  return request<{ config?: TalentSampleConfig }, UpdateTalentSampleConfigReq>(
    _POST,
    "/admin/talent/sample-config/update"
  )(params);
};

/**
 * 保存未关联联盟数据的达人联盟信息
 * POST /admin/talent/leagueData/save
 */
export const saveTalentLeagueData = (params: SaveTalentLeagueDataReq) => {
  return request<Record<string, never>, SaveTalentLeagueDataReq>(_POST, "/admin/talent/leagueData/save")(params);
};

/**
 * 获取达人操作记录
 * GET /admin/talent/operation_records
 */
export const getInfluencerOperationRecords = (params?: OperationDetailRequest) => {
  return request<{ operationRecords: OperationDetailRecord[] }, OperationDetailRequest>(
    _GET,
    "/admin/talent/operation_records"
  )(params || {});
};

/**
 * 达人禁用/解除
 * POST /admin/talent/ban
 */
export const influencerBan = (params?: OperationStateRequest) => {
  return request<null, OperationStateRequest>(_POST, "/admin/talent/ban")(params || {});
};

/**
 * 邀请权限管理
 * POST /admin/invitePermission/list
 */
export const getInvitePermissionList = (params?: InvitePermissionListRequest) => {
  return request<InvitePermissionListRes, InvitePermissionListRequest>(
    _POST,
    "/admin/invitePermission/list"
  )(params || {});
};

/**
 * 查询邀请人的被邀请列表
 * POST /admin/invitePermission/listInvitees
 */
export const getInvitePermissionListInvitees = (params?: InvitePermissionListInviteesRequest) => {
  return request<InvitePermissionListInviteesRes, InvitePermissionListInviteesRequest>(
    _POST,
    "/admin/invitePermission/listInvitees"
  )(params || {});
};

/**
 * 添加邀请权限
 * POST /admin/invitePermission/updateStatus
 */
export const updateInvitePermissionStatus = (params?: InvitePermissionStatusRequest) => {
  return request<null, InvitePermissionStatusRequest>(_POST, "/admin/invitePermission/updateStatus")(params || {});
};

/**
 * 获取模数配置
 * POST /admin/invitePermission/getModSetting
 */
export const getModSetting = () =>
  request<ModSettingResponse, null>(_POST, "/admin/invitePermission/getModSetting")(null);

/**
 * 保存模数
 * POST /admin/invitePermission/saveModSetting
 */
export const saveModSetting = (params: ModSettingResponse) =>
  request<null, ModSettingResponse>(_POST, "/admin/invitePermission/saveModSetting")(params);

const influencerAPis = {
  getInfluencerTalentList,
  getInfluencerDetail,
  updateTalentSampleConfig,
  saveTalentLeagueData,
  getInfluencerOperationRecords,
  getInvitePermissionListInvitees,
  getInvitePermissionList,
  updateInvitePermissionStatus,
  influencerBan,
  getModSetting,
  saveModSetting,
};

export default influencerAPis;
